import { FinanceModel, FinanceModelTypes } from '@/services/api/contracts/financemodels';
import { useFinanceModels, usePeripheralFinanceModels } from '@/services/api/hooks/financemodels';
import {
  usePoSelectedPeripheralFinanceModelName,
  usePoSelectGlobalFinanceModel
} from '@/services/purchaseOrders/usePurchaseOrderService';
import { FC, useMemo } from 'react';
import { CAPITAL_PURCHASE_ID, CAPITAL_PURCHASE_NAME } from '../../financeModels/const';
import { PurchaseOrderFinanceModelCard } from './PurchaseOrderFinanceModelCard';

interface Props {
  categoryId: string;
  manufacturerIds: string[];
}

const getUniqueModelsByName = (models: FinanceModel[]) =>
  Array.from(
    models
      .reduce((map, item: FinanceModel) => {
        if (!map.has(item.name)) {
          map.set(item.name, item);
        }
        return map;
      }, new Map<string, FinanceModel>())
      .values()
  );

const isModelsMissing = (modelsForCategory: FinanceModel[], manufacturerIds: string[]) => {
  const groups = modelsForCategory.reduce<Record<string, Set<string>>>((acc, item: FinanceModel) => {
    if (!acc[item.manufacturer.name]) {
      acc[item.manufacturer.name] = new Set();
    }
    acc[item.manufacturer.name].add(item.name);
    return acc;
  }, {});

  const counts = Object.values(groups).map(group => group.size);
  return new Set(counts).size === 0;
};

export const PurchaseOrderGlobalFinanceModelSection: FC<Props> = () => {
  // NOTE: Finance models are now global (i.e. peripheral and main devices use the same finance model)
  // So under the assumption that we can use the selected finance model name for the peripheral.
  const selectedModelName = usePoSelectedPeripheralFinanceModelName();

  const models = useFinanceModels();
  const peripheralFinanceModels = usePeripheralFinanceModels();

  const uniqueModels = useMemo(() => {
    const uniqueMainDeviceModels = getUniqueModelsByName(models).filter(
      m => m.financeModelType !== FinanceModelTypes.FMV || m.name === selectedModelName
    );

    const commonEntries = uniqueMainDeviceModels.filter(item1 =>
      peripheralFinanceModels.some(item2 => item1.name === item2.name)
    );

    return commonEntries;
  }, [models, selectedModelName]);

  const selectGlobalModel = usePoSelectGlobalFinanceModel();

  const onModelChange = (id: string) => {
    if (id === CAPITAL_PURCHASE_ID) {
      selectGlobalModel(CAPITAL_PURCHASE_NAME);
    } else {
      const model = models.find(m => m.id === id);
      if (!model) throw new Error(`Missing model with id: ${id}`);
      selectGlobalModel(model.name);
    }
  };

  return (
    <PurchaseOrderFinanceModelCard
      models={uniqueModels}
      selectedModelName={selectedModelName}
      onChange={onModelChange}
    />
  );
};
